import React, { createContext, useContext, useEffect, useMemo, useReducer, useState } from 'react';
import PropTypes from 'prop-types';
import Sidebar from '../components/Navigation/Sidebar';
import Header from '../components/Navigation/Header';
import { LoaderContext } from '../context/loader-context';
import { ErrorContext } from './error-context';
import ErrorWrapper from '../components/ErrorWrapper/ErrorWrapper';
import Loader from '../components/Loader/Loader';
import { LangContext } from '../context/lang-context';

export const NavigationContext = createContext();

const initialState = {
    permissions: [],
    routes: [],
    enableReports: [],
    isAllowed: false,
    isLoad: false
};

const getEnableReports = permissions =>
    (permissions || [])
        .filter(item => item && /VD_REPORT_\d{1,4}/.test(item.Name))
        .map(item => item.Name);

const permissionReducer = (state, action) => {
    switch (action.type) {
        case 'syncFromApp': {
            const permissions = (action.payload && action.payload.permissions) || [];
            const isAllowed = !!(action.payload && action.payload.isAllowed);

            return {
                ...state,
                permissions,
                enableReports: getEnableReports(permissions),
                isAllowed,
                isLoad: true
            };
        }

        case 'setPermission': {
            const permissions = action.payload || [];
            return {
                ...state,
                permissions,
                enableReports: getEnableReports(permissions)
            };
        }

        case 'setReports':
            return {
                ...state,
                enableReports: getEnableReports(action.payload || [])
            };

        case 'setRoutes':
            return {
                ...state,
                routes: action.payload || []
            };

        case 'setRoute':
            return {
                ...state,
                routes: [...state.routes, action.payload]
            };

        case 'setIsAllowed':
            return {
                ...state,
                isAllowed: !!action.payload
            };

        case 'dispatchIsLoad':
            return {
                ...state,
                isLoad: true
            };

        default:
            return state;
    }
};

const NavigationContextProvider = ({
    children,
    permissions,
    customerName,
    isAllowed,
    isConsole,
    isLocalDb,
    isMobile
}) => {
    const { TRL_Pack } = useContext(LangContext);
    const { fatalError } = useContext(ErrorContext);
    const { loader } = useContext(LoaderContext);

    const [sectionModal] = useState(null);
    const [showSideMenu, setShowSideMenu] = useState(true);
    const [headerData, setHeaderData] = useState({});
    const [width, setWidth] = useState(localStorage.getItem('sidebarWidth') || 'md');

    const [state, dispatch] = useReducer(permissionReducer, initialState);

    useEffect(() => {
        dispatch({
            type: 'syncFromApp',
            payload: {
                permissions,
                isAllowed
            }
        });
    }, [permissions, isAllowed]);

    const toggleSidebarWidth = () => {
        setWidth(prev => {
            const nextWidth = prev === 'md' ? 'sm' : 'md';
            localStorage.setItem('sidebarWidth', nextWidth);
            return nextWidth;
        });
    };

    const navlinks = useMemo(() => {
        const links = [];
        const pushUnique = (link) => {if (!links.some(l => l.path === link.path)) links.push(link)};
        const currentPermissions = state.permissions || [];

        if (isLocalDb) {
            currentPermissions.forEach(permission => {
                switch (permission.Name) {
                    case 'VD_PRODUCTS':
                        pushUnique({ text: TRL_Pack.navbar.products, path: '/products', icon: 'fas fa-apple-whole' });
                        break;
                    case 'VD_MACHINE_CONFIG':
                        pushUnique({ text: TRL_Pack.navbar.supply, path: '/supply', icon: 'fas fa-cart-plus' });
                        break;
                    case 'VD_MACHINE_RECHARGE':
                        pushUnique({ text: TRL_Pack.shelves.configHeader, path: '/config', icon: 'fas fa-cog' });
                        break;
                    case 'VD_EVENTS':
                        pushUnique({ text: TRL_Pack.events.events, path: '/events', icon: 'fas fa-calendar' });
                        break;
                    case 'VD_USERS':
                        pushUnique({ text: TRL_Pack.navbar.users, path: '/users', icon: 'fas fa-users' });
                        break;
                    default:
                        break;
                }
            });
        } else if (isConsole) {
            links.push(
                { text: 'VPN Management', path: '/vpn', icon: 'fas fa-calendar'},
                { text: 'Server status', path: '/server-status', icon: 'fas fa-server' },
                { text: TRL_Pack.navigation.monitoring, path: '/monitoring', icon: 'fa fa-desktop' },
                { text: TRL_Pack.navigation.machines, path: '/machines', icon: 'fas fa-tablet-alt' },
                { text: TRL_Pack.navigation.terminals, path: '/terminals', icon: 'fas fa-credit-card' },
                { text: TRL_Pack.navigation.trxs, path: '/trxs', icon: 'fas fa-credit-card' },
                { text: TRL_Pack.navigation.clients, path: '/clients', icon: 'fas fa-users-cog' },
                { text: 'NEXGO', path: '/nexgo', icon: 'fas fa-network-wired' },
                { text: 'Karty prepaid', path: '/prepaids', icon: 'fas fa-address-card' },
                { text: 'Fakturowanie', path: '/invoices', icon: 'fas fa-calendar' }
            );
        } else if (isMobile) {
            links.push({text: TRL_Pack.navigation.machines, path: '/machines', icon: 'fas fa-tablet'});
        } else {
            currentPermissions.forEach(permission => {
                switch (permission.Name) {
                    case 'VD_PRODUCTS':
                        pushUnique({ text: TRL_Pack.navbar.products, path: '/products', icon: 'fas fa-apple-whole' });
                        break;
                    case 'VD_PAS':
                        pushUnique({ text: TRL_Pack.navbar.pas, path: '/pas', icon: 'fas fa-receipt' });
                        break;
                    case 'VD_WIS':
                        pushUnique({ text: TRL_Pack.navbar.wis, path: '/wis', icon: 'fas fa-compass' });
                        break;
                    case 'VD_ADVANCED_WIS':
                        pushUnique({ text: 'Rozszerzony widok wizyt', path: '/advanced-wis', icon: 'fas fa-table' });
                        break;
                    case 'VD_KPS':
                        pushUnique({ text: TRL_Pack.navbar.kps, path: '/kps', icon: 'fas fa-coins' });
                        break;
                    case 'VD_DOS':
                        pushUnique({ text: TRL_Pack.navbar.dos, path: '/dos', icon: 'fas fa-forward' });
                        break;
                    case 'VD_ROS':
                        pushUnique({ text: TRL_Pack.navbar.ros, path: '/ros', icon: 'fas fa-backward' });
                        break;
                    case 'VD_INMS':
                        pushUnique({ text: TRL_Pack.navbar.inms, path: '/inms', icon: 'fas fa-prescription-bottle' });
                        break;
                    case 'VD_INS':
                        pushUnique({ text: TRL_Pack.navbar.ins, path: '/ins', icon: 'fas fa-pallet' });
                        break;
                    case 'VD_WZS':
                        pushUnique({ text: TRL_Pack.navbar.wzs, path: '/wzs', icon: 'fas fa-arrow-alt-circle-right' });
                        break;
                    case 'VD_PZS':
                        pushUnique({ text: TRL_Pack.navbar.pzs, path: '/pzs', icon: 'fas fa-arrow-alt-circle-left' });
                        break;
                    case 'VD_STS':
                        pushUnique({ text: TRL_Pack.navbar.sts, path: '/sts', icon: 'fas fa-skull-crossbones' });
                        break;
                    case 'VD_ZWS':
                        pushUnique({ text: TRL_Pack.navbar.zws, path: '/zws', icon: 'fas fa-chalkboard-teacher' });
                        break;
                    case 'VD_FZS':
                        pushUnique({ text: TRL_Pack.navbar.fzs, path: '/fzs', icon: 'fas fa-cart-plus' });
                        break;
                    case 'VD_KOS':
                        pushUnique({ text: TRL_Pack.navbar.kos, path: '/kos', icon: 'fas fa-eraser' });
                        break;
                    case 'VD_MACHINES':
                        pushUnique({ text: TRL_Pack.navigation.machines, path: '/machines', icon: 'fas fa-tablet' });
                        break;
                    case 'VD_TAGS':
                        pushUnique({ text: "Grupy tagów", path: '/tags', icon: 'fas fa-tags' });
                        break;
                    case 'VD_PAYMENT_CODES':
                        pushUnique({ text: TRL_Pack.codes.codes, path: '/codes', icon: 'fas fa-qrcode' });
                        break;
                    case 'VD_PERSONALISE':
                        pushUnique({ text: TRL_Pack.navbar.personalise, path: '/personalise', icon: 'fas fa-sliders-h' });
                        break;
                    case 'VD_LOCATIONS':
                        pushUnique({ text: TRL_Pack.navbar.locations, path: '/machinelocations', icon: 'fas fa-map-marker-alt' });
                        break;
                    case 'VD_USERS':
                        pushUnique({ text: TRL_Pack.navbar.users, path: '/users', icon: 'fas fa-users' });
                        break;
                    case 'VD_REPORTS':
                        pushUnique({ text: 'Raporty', path: '/reports', icon: 'fas fa-download' });
                        break;
                    case 'VD_TERMINALS':
                        pushUnique({ text: TRL_Pack.navigation.terminals, path: '/terminals', icon: 'fas fa-credit-card' });
                        break;
                    case 'VD_TRXS':
                        pushUnique({ text: TRL_Pack.navigation.trxs, path: '/trxs', icon: 'fas fa-list-alt' });
                        break;
                    case 'VD_MONITORING':
                        pushUnique({ text: TRL_Pack.navigation.monitoring, path: '/monitoring', icon: 'fa fa-desktop' });
                        break;
                    case 'VD_LASTVI':
                        pushUnique({ text: 'Ostatnie wizyty', path: '/lastvi', icon: 'fas fa-dolly' });
                        break;
                    case 'VD_MM':
                        pushUnique({ text: 'Przesunięcia magazynowe', path: '/mms', icon: 'fas fa-exchange-alt' });
                        break;
                    case 'VD_WAREHOUSES':
                        pushUnique({ text: 'Magazyny', path: '/whs', icon: 'fas fa-warehouse' });
                        break;
                    case 'VD_MACHINE_TYPES':
                        pushUnique({ text: 'Typy automatów', path: '/machinetypes', icon: 'fas fa-poll-h' });
                        break;
                    case 'VD_BAGS':
                        pushUnique({ text: 'Worki na monety', path: '/bags', icon: 'fa-solid fa-sack-dollar' });
                        break;
                    case 'VD_CONTRACTORS':
                        pushUnique({ text: 'Kontrahenci', path: '/contractors', icon: 'fas fa-solid fa-user-tie' });
                        break;
                    case 'VD_BASKETS':
                        pushUnique({ text: 'Zestawy promocyjne', path: '/promotions', icon: 'fas fa-percent' });
                        break;
                    case 'VD_EVENTS':
                        pushUnique({ text: 'Typy zdarzeń', path: '/events', icon: 'fas fa-exclamation-triangle' });
                        break;
                    case 'VD_MANUFACTURERS':
                        pushUnique({ text: 'Producenci', path: '/manufacturers', icon: 'fas fa-industry' });
                        break;
                    case 'VD_CUSTOMER':
                        pushUnique({ text: 'Dane firmy', path: '/customer', icon: 'fas fa-building' });
                        break;
                    case 'VD_PREPAID':
                        pushUnique({ text: 'Karty prepaid', path: '/prepaids', icon: 'fas fa-address-card' });
                        break;
                    case 'VD_WKF':
                        pushUnique({ text: 'Kasy fiskalne', path: '/wkfs', icon: 'fas fa-cash-register' });
                        break;
                    case 'VD_CR':
                        pushUnique({ text: 'Powody korekt', path: '/corection-reasons', icon: 'fas fa-undo-alt' });
                        break;
                    case 'VD_ERRORS':
                        pushUnique({ text: 'Błędy', path: '/errors', icon: 'fas fa-solid fa-bug' });
                        break;
                    case 'VD_MAINTENANCE_TYPE':
                        pushUnique({ text: 'Tryby obsługi', path: '/maintenance-type', icon: 'fas fa-cogs' });
                        break;
                    case "VD_ADVERTISES":
                        pushUnique({text: 'Reklamy', path: '/advertises', icon: 'fas fa-file-arrow-up'})
                        break;
                    case "VD_NOTIFICATIONS":
                        pushUnique({text: 'Powiadomienia', path: '/notifications', icon: 'fas fa-bell'}
                        )
                        break;
                    default:
                        break;
                }
            });
        }

        if (!isLocalDb && !state.isAllowed) {
            pushUnique({ text: 'Uzupełnienie danych', path: '/wkfs/configuration', icon: 'fas fa-user' });
        }

        pushUnique({ text: TRL_Pack.navigation.logout, path: '/logout', icon: 'fas fa-sign-out-alt' });

        return links;
    }, [TRL_Pack, state.permissions, state.isAllowed, isConsole, isLocalDb, isMobile]);

    return (
        <NavigationContext.Provider
            value={{
                state,
                navlinks,
                dispatch,
                setHeaderData,
                setShowSideMenu,
                customerName
            }}
        >
            <div className={showSideMenu ? 'd-block' : 'd-none'}>
                <Sidebar width={width} navlinks={navlinks} />
            </div>

            <div className="d-flex flex-column flex-grow-1" style={{ minWidth: 0 }}>
                <Header
                    toggleSidebar={toggleSidebarWidth}
                    headerData={headerData}
                    navlinks={navlinks}
                    customerName={customerName}
                    headerClassName={showSideMenu ? 'shadow d-flex flex-column justify-content-center p-0' : 'd-none'}
                />

                {fatalError ? (
                    <ErrorWrapper />
                ) : (
                    <div className={`d-flex flex-column flex-grow-1 ${loader ? 'position-static' : ''}`}>
                        {sectionModal}
                        {loader && <Loader />}
                        <div className={`${!isMobile ? 'p-1' : 'py-1'} h-100 d-flex flex-column`}>
                            {children}
                        </div>
                    </div>
                )}
            </div>
        </NavigationContext.Provider>
    );
};

NavigationContextProvider.propTypes = {
    children: PropTypes.node,
    permissions: PropTypes.array,
    customerName: PropTypes.string,
    isAllowed: PropTypes.bool,
    isConsole: PropTypes.bool,
    isLocalDb: PropTypes.bool,
    isMobile: PropTypes.bool
};

export default NavigationContextProvider;